import React, { useMemo, useState } from 'react';
import { Box, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import SearchFilterInput from './SearchFilterInput';
import { countMarkdownSearchMatches, renderSimpleMarkdown } from '../lib/renderSimpleMarkdown';

export default function MarkdownDocViewer({
  markdown,
  emptyText = 'Aucun contenu à afficher.',
  noResultText = 'Aucun résultat pour cette recherche.',
  scrollHeight = 360,
  fill = false,
  showSearch = true,
  searchPlaceholder = 'Rechercher dans la notice…',
  searchName = 'jlfc-markdown-doc-search',
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
}) {
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const searchQuery = controlledSearchQuery !== undefined ? controlledSearchQuery : internalSearchQuery;

  const setSearchQuery = (value) => {
    if (onSearchQueryChange) onSearchQueryChange(value);
    else setInternalSearchQuery(value);
  };

  const applySearchFilter = showSearch || controlledSearchQuery !== undefined;

  const blocks = useMemo(
    () => renderSimpleMarkdown(markdown, { searchQuery: applySearchFilter ? searchQuery : '' }),
    [markdown, searchQuery, applySearchFilter],
  );

  const matchCount = useMemo(
    () => (applySearchFilter ? countMarkdownSearchMatches(markdown, searchQuery) : 0),
    [markdown, searchQuery, applySearchFilter],
  );

  const hasContent = Boolean(String(markdown || '').trim());
  const hasResults = blocks.length > 0;

  const searchOffset = showSearch ? 72 : 0;
  const areaHeight =
    typeof scrollHeight === 'string'
      ? scrollHeight
      : Math.max(120, scrollHeight - searchOffset);

  const rootStyle = fill
    ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
    : undefined;

  const paperStyle = fill
    ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
    : { overflow: 'hidden' };

  return (
    <Stack gap="sm" style={rootStyle}>
      {showSearch ? (
        <>
          <SearchFilterInput
            placeholder={searchPlaceholder}
            name={searchName}
            aria-label="Rechercher dans la documentation"
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flexShrink: 0 }}
          />

          {searchQuery.trim() ? (
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
              {matchCount > 0
                ? `${matchCount} ligne${matchCount > 1 ? 's' : ''} correspondante${matchCount > 1 ? 's' : ''}`
                : noResultText}
            </Text>
          ) : null}
        </>
      ) : null}

      <Paper withBorder p="md" radius="md" style={paperStyle}>
        {fill ? (
          <ScrollArea flex={1} type="scroll" offsetScrollbars style={{ minHeight: 0 }}>
            <Box pb="xl">
              <DocBody
                hasContent={hasContent}
                hasResults={hasResults}
                emptyText={emptyText}
                noResultText={noResultText}
                blocks={blocks}
              />
            </Box>
          </ScrollArea>
        ) : (
          <ScrollArea h={areaHeight} type="scroll" offsetScrollbars>
            <Box pb="xl">
              <DocBody
                hasContent={hasContent}
                hasResults={hasResults}
                emptyText={emptyText}
                noResultText={noResultText}
                blocks={blocks}
              />
            </Box>
          </ScrollArea>
        )}
      </Paper>
    </Stack>
  );
}

function DocBody({ hasContent, hasResults, emptyText, noResultText, blocks }) {
  if (!hasContent) {
    return (
      <Text size="sm" c="dimmed">
        {emptyText}
      </Text>
    );
  }
  if (!hasResults) {
    return (
      <Text size="sm" c="dimmed">
        {noResultText}
      </Text>
    );
  }
  return (
    <Box pr="xs">
      <Stack gap="xs">{blocks}</Stack>
    </Box>
  );
}
